import {
  ADD_TRANSACTION_PAYMENT_METHOD_NAME,
  ARRAY_DEFAULT,
  FUNC_DEFAULT,
  METHOD_PAYTO,
  OBJECT_DEFAULT,
} from 'constants';
import {
  FORM_ADD_PAYTO_BSB_NAME,
  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME,
  PAYTO_ACCOUNT_ACTIVE_STATUS,
  PAYTO_LOG_TAG,
} from 'features/to/const';
import usePaytoAPI from 'features/to/hooks/payment/usePaytoAPI';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import useAddTransaction from 'hooks/form-add-transaction/useAddTransaction';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import useFxAddTransaction from 'hooks/fx/useFxAddTransaction';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useCommonStore from 'hooks/useCommonStore';
import usePage from 'hooks/usePage';
import { isArray, isEmpty } from 'lodash';
import React from 'react';

const withAddPaytoAccountsAgreement = (Component) => {
  const AddPaytoAccountsAgreementComponent = (props) => {
    const {
      paytoAccountInformation,
      setPaytoAccountInformation,
      setPaytoAccountsAgreement,
      paytoAccountsAgreement,
    } = usePaytoStore();
    const {
      fetchPaytoAccountsAgreement,
      payloadTransformFromPaytoAccountAgreement,
      payloadTransformFromPaytoAccountInformation,
    } = usePaytoAPI();
    const { getTotalToPay } = useAddTransaction();
    const { isFxAsGuest } = useFxAddTransaction();
    const { setLoading } = useCommonStore();
    const { isAddTransactionPage, isFxAddTransactionPage } = usePage();
    const {
      setPaymentMethodName: setFXPaymentMethodName,
      paymentMethodName,
      getTotalPayAmount,
    } = useFxFormAddTransactionStore();
    const { paymentMethod, setPaymentMethod, isPaytoBlocked } =
      useFormAddTransactionStore();

    const totalToPay = isAddTransactionPage
      ? getTotalToPay()
      : getTotalPayAmount();

    const handleFXPaymentMethodAutoSelect = () => {
      if (!isFxAddTransactionPage) return;
      if (paymentMethodName === METHOD_PAYTO) {
        setFXPaymentMethodName('');
      }
    };
    const handleTOPaymentMethodAutoSelect = () => {
      if (!isAddTransactionPage) return;
      if (
        paymentMethod?.[ADD_TRANSACTION_PAYMENT_METHOD_NAME] === METHOD_PAYTO
      ) {
        setPaymentMethod({
          ...paymentMethod,
          [ADD_TRANSACTION_PAYMENT_METHOD_NAME]: '',
        });
      }
    };
    const onResetPaymentMethod = () => {
      if (isAddTransactionPage) {
        handleTOPaymentMethodAutoSelect();
      }
      if (isFxAddTransactionPage) {
        handleFXPaymentMethodAutoSelect();
      }
    };

    const fetchPaytoAccountsAgreementLatest = (
      callback = FUNC_DEFAULT,
      isCheckBlock = true
    ) => {
      if (isFxAsGuest) {
        setLoading(false);
        return;
      }
      fetchPaytoAccountsAgreement((data) => {
        setPaytoAccountsAgreement(data);
        if (!isEmpty(data)) {
          if (
            isCheckBlock &&
            data?.some((accountAgreement) => accountAgreement?.blocked)
          ) {
            onResetPaymentMethod();
            setLoading(false);
            return;
          }
          callback(data);
        } else {
          onResetPaymentMethod();
        }
        setLoading(false);
      });
    };

    const handlePaytoAccountAgreementItemOnSelect = (
      accountAgreement = OBJECT_DEFAULT,
      payloadExt = OBJECT_DEFAULT
    ) => {
      if (isEmpty(accountAgreement)) {
        onResetPaymentMethod();
        return;
      }
      const payload = {
        ...payloadTransformFromPaytoAccountAgreement(accountAgreement),
        ...payloadExt,
      };
      setPaytoAccountInformation(payload);
    };

    const getAccountAgreementsFiltered = (accountAgreements) => {
      return accountAgreements.filter(
        ({ blocked, paymentAgreementStatus }) =>
          !blocked && paymentAgreementStatus === PAYTO_ACCOUNT_ACTIVE_STATUS
      );
    };
    const getAccountAgreementValid = (accountAgreements) => {
      if (isEmpty(accountAgreements) || !isArray(accountAgreements))
        return null;
      if (accountAgreements?.length === 1) {
        if (
          !accountAgreements?.[0]?.paymentAgreementUID ||
          accountAgreements?.[0]?.paymentAgreementUID === 'payid' ||
          accountAgreements?.[0]?.paymentAgreementUID === 'other'
        )
          return null;
        if (Number(accountAgreements?.[0]?.maxAmount) >= totalToPay)
          return accountAgreements?.[0];
      }

      const accountAgreementSelectedValid = accountAgreements.find(
        ({ maxAmount, paymentAgreementStatus, paymentAgreementUID }) =>
          paymentAgreementStatus === PAYTO_ACCOUNT_ACTIVE_STATUS &&
          Number(maxAmount) >= totalToPay &&
          paymentAgreementUID === paytoAccountInformation?.id
      );
      if (!isEmpty(accountAgreementSelectedValid)) {
        return accountAgreementSelectedValid;
      }
      return accountAgreements.find(
        ({ maxAmount, paymentAgreementStatus, pendingAmend }) =>
          paymentAgreementStatus === PAYTO_ACCOUNT_ACTIVE_STATUS &&
          Number(maxAmount) >= totalToPay
      );
    };
    const handlePaytoAccountAgreementItemValidOnSelect = (
      accountAgreements = ARRAY_DEFAULT,
      payloadExt = OBJECT_DEFAULT
    ) => {
      if (isEmpty(accountAgreements)) {
        setPaytoAccountInformation(null);
        onResetPaymentMethod();
        return;
      }
      const accountAgreementsFiltered =
        getAccountAgreementsFiltered(accountAgreements);
      if (isEmpty(accountAgreementsFiltered)) {
        setPaytoAccountInformation(null);
        return;
      }
      const accountAgreementValid = getAccountAgreementValid(
        accountAgreementsFiltered
      );
      if (!isEmpty(accountAgreementValid)) {
        handlePaytoAccountAgreementItemOnSelect(
          accountAgreementValid,
          payloadExt
        );
      } else {
        setPaytoAccountInformation(null);
      }
    };

    const handlePaytoAccountAgreementOnSelect = (
      item = OBJECT_DEFAULT,
      payloadExt = OBJECT_DEFAULT,
      isDelete = false
    ) => {
      if (isPaytoBlocked) {
        console.log(
          `⭐ ${PAYTO_LOG_TAG} 👉 handlePaytoAccountAgreementOnSelect ➡️ isPaytoBlocked ↩`,
          isPaytoBlocked
        );
        return;
      }

      if (!isDelete) {
        const itemTransform =
          item?.[FORM_ADD_PAYTO_BSB_NAME] ||
          item?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME]
            ? payloadTransformFromPaytoAccountInformation(item)
            : item;
        console.log(
          `⭐ ${PAYTO_LOG_TAG} 👉 handlePaytoAccountAgreementOnSelect initial ➡️ item ↩`,
          itemTransform
        );
        const itemValid = getAccountAgreementValid([itemTransform]);
        if (!isEmpty(itemTransform) && !isEmpty(itemValid)) {
          console.log(
            `⭐ ${PAYTO_LOG_TAG} 👉 handlePaytoAccountAgreementOnSelect 1 ➡️ item ↩`,
            itemTransform
          );
          handlePaytoAccountAgreementItemOnSelect(itemTransform, payloadExt);
          return;
        }

        const accountAgreement = payloadTransformFromPaytoAccountInformation(
          paytoAccountInformation
        );
        const accountAgreementCurrent = paytoAccountsAgreement?.find(
          (agreement) =>
            agreement?.paymentAgreementUID ===
            accountAgreement?.paymentAgreementUID
        );
        const accountAgreementValid = !isEmpty(accountAgreementCurrent)
          ? getAccountAgreementValid([accountAgreementCurrent])
          : null;
        if (isEmpty(itemTransform) && !isEmpty(accountAgreementValid)) {
          console.log(
            `⭐ ${PAYTO_LOG_TAG} 👉 handlePaytoAccountAgreementOnSelect 2 ➡️ item ↩`,
            accountAgreementCurrent
          );
          handlePaytoAccountAgreementItemOnSelect(
            accountAgreementCurrent,
            payloadExt
          );
          return;
        }
      }

      console.log(
        `⭐ withAddPaytoAccountsAgreement 👉 handlePaytoAccountAgreementOnSelect ↩`
      );
      fetchPaytoAccountsAgreementLatest((accountsAgreement) => {
        console.log(
          `⭐ ${PAYTO_LOG_TAG} 👉 handlePaytoAccountAgreementOnSelect 3`
        );
        handlePaytoAccountAgreementItemValidOnSelect(
          accountsAgreement,
          payloadExt
        );
      });
    };

    const newProps = {
      ...props,
      fetchPaytoAccountsAgreementLatest,
      onSelect: handlePaytoAccountAgreementOnSelect,
      onSelectValid: handlePaytoAccountAgreementItemValidOnSelect,
    };
    return <Component {...newProps} />;
  };
  return AddPaytoAccountsAgreementComponent;
};

export default withAddPaytoAccountsAgreement;
